/**
 * What a buyer tells suppliers about a service before they quote it (SCRUM-112, v2 in SCRUM-117).
 * The visit and requirements are no longer asked but older services still carry them; requirements
 * travel as stable codes.
 */
export enum TechnicalVisit {
  NOT_NEEDED = "NOT_NEEDED",
  OPTIONAL = "OPTIONAL",
  MANDATORY = "MANDATORY",
}

/** The units a service line is quoted in (SCRUM-113). */
export enum ServiceUnit {
  HOUR = "HOUR",
  WORKDAY = "WORKDAY",
  DAY = "DAY",
  MONTH = "MONTH",
  VISIT = "VISIT",
  LUMP_SUM = "LUMP_SUM",
}

/**
 * A line suppliers price inside one service of one quotation. It exists only in that
 * quotation: never a catalogue item. `code` (L1, L2…) is given by the backend on save.
 */
export interface ServiceLine {
  code?: string;
  description: string;
  quantity: number | string;
  unit: ServiceUnit;
  /** SCRUM-117: what the line includes — standard, equipment, conditions. */
  note?: string | null;
  /** SCRUM-117: the line's own files. */
  files?: ServiceLineFile[];
}

export interface ServiceLineFile {
  name: string;
  fileURL: string;
}

export interface ServiceFile {
  id?: string;
  name: string;
  fileURL: string;
  type: string;
}

export interface ServiceDetail {
  /** SCRUM-117: the service's name ("Nombre del servicio"); on a single-service quotation, its name. */
  serviceTitle?: string;
  serviceObjective?: string;
  serviceScope?: string;
  serviceDeliverables?: string;
  technicalVisit?: TechnicalVisit;
  technicalVisitDate?: string | null;
  serviceRequirements?: string[];
  serviceFiles?: ServiceFile[];
  serviceLines?: ServiceLine[];
}

/** One service of a quotation as the "Servicios a cotizar" card shows it (SCRUM-115). */
export interface ServiceToQuote {
  orderId?: string;
  purchaseRequestItemId?: string | null;
  serviceName: string;
  /** SCRUM-117: the name the buyer gave the service; older services have none. */
  title?: string | null;
  objective?: string | null;
  scope?: string | null;
  deliverables?: string | null;
  technicalVisit?: TechnicalVisit | null;
  technicalVisitDate?: string | null;
  requirements: string[];
  files: { name: string; fileURL: string }[];
  lines: ServiceLine[];
}

/**
 * What a supplier actually prices in an order (the backend's `cotItems`): a service quoted by
 * line becomes its lines, grouped under the service; anything else is one line.
 */
export interface QuotedLine {
  group: string | null;
  /** SCRUM-117: the name the buyer gave the line's service. */
  groupTitle: string | null;
  code: string | null;
  description: string;
  quantity: number | null;
  unit: string | null;
  note: string | null;
  files: ServiceLineFile[];
}
